import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { TwoFactorVerificationResponse, UserSettingsService } from '../../../../../../services/members/user-settings.service';
import { ValidationMessageService } from '../../../../../../services/validation-message.service';
import { GrowlerService } from '../../../../../../services/growler.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export interface ConfirmTwoFactorInput
{
    type: 'google' | 'sms' | null;
    userId: number;
    canGoBack?: boolean;
}

export interface ConfirmTwoFactorOutput
{
    success: boolean;
    type?: 'google' | 'sms';
    token?: string;
}

@UntilDestroy()
@Component({
    selector: 'app-confirm-2fa-code',
    templateUrl: './confirm-2fa-code.component.html',
})
export class Confirm2faCodeComponent implements OnInit
{
    // Form model lets.
    isActive = false;
    isSubmitting = false;
    type: 'google' | 'sms' | null = null;
    canGoBack = true;
    isSendingSms = false;

    control: FormControl;

    constructor(
            private _builder: FormBuilder,
            private _userSettingsService: UserSettingsService,
            private _validationMessageService: ValidationMessageService,
            private _growler: GrowlerService,
            private _dialogRef: MatDialogRef<Confirm2faCodeComponent>,
            @Inject(MAT_DIALOG_DATA) public data: ConfirmTwoFactorInput,
    ) {
    }

    ngOnInit(): void {
        this.type = this.data.type;

        // If triggering an SMS validation, make a call to send one
        if (this.type === 'sms') {
            this.setSmsType();
        }

        // Prevent going back to choose a different auth type if we need a specific type validated
        // for example, force google auth if trying to remove google auth
        if (this.data.hasOwnProperty('canGoBack')) {
            this.canGoBack = this.data.canGoBack;
        }

        // Codes should be 6 digits from either source
        this.control = new FormControl('', [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(6),
        ]);
    }

    /** Toggle SMS type, ask server to send a code */
    setSmsType() {
        this.isSendingSms = true;
        this._userSettingsService.sendSmsVerificationCode(this.data.userId).subscribe({
            next: () => {
                this.isSendingSms = false;
                this.type = 'sms';
            },
            error: () => {
                this._growler.error('Error', 'We encountered an error while trying to send the code.');
                this.isSendingSms = false;
            },
        });

    }

    /** Toggle google auth type */
    setGoogleType() {
        this.type = 'google';
    }

    /** Submit the code */
    submit() {
        if (!this.control.valid) {
            this._growler.error('Error', 'Please enter a valid 2FA code.');
            return;
        }

        this.isSubmitting = true;

        if (this.type === 'google') {
            this._userSettingsService.verifyGoogleAuthCode(this.control.value, this.data.userId)
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: (response: TwoFactorVerificationResponse) => {
                            this._dialogRef.close(<ConfirmTwoFactorOutput>{
                                success: true,
                                type: this.type,
                                token: response.verificationKey,
                            });
                        },
                        error: () => {
                            this.isSubmitting = false;
                            this._growler.error('Error', 'We were unable to verify that code. Please try again.');
                        },
                    });
        } else if (this.type === 'sms') {
            this._userSettingsService.verifySmsCode(this.control.value, this.data.userId).pipe(untilDestroyed(this)).subscribe({
                next: (response: TwoFactorVerificationResponse) => {
                    this._dialogRef.close(<ConfirmTwoFactorOutput>{
                        success: true,
                        type: this.type,
                        token: response.verificationKey,
                    });
                },
                error: () => {
                    this.isSubmitting = false;
                    this._growler.error('Error', 'We were unable to verify that code. Please try again.');
                },
            });
        }
    }
}
